
import type { ReleaseNote } from '../../types';
import { renderMarkdown } from '../../utils/markdown';

interface ReleaseNoteCardProps {
  note: ReleaseNote;
  isNew: boolean;
}

const typeColors: Record<ReleaseNote['type'], string> = {
  feature: '#22c55e',
  improvement: '#3b82f6',
  bugfix: '#eab308',
  breaking: '#ef4444',
};

const typeLabels: Record<ReleaseNote['type'], string> = {
  feature: 'Feature',
  improvement: 'Improvement',
  bugfix: 'Bug Fix',
  breaking: 'Breaking',
};

export function ReleaseNoteCard({ note, isNew }: ReleaseNoteCardProps) {
  const color = typeColors[note.type] ?? '#6b7280';
  const label = typeLabels[note.type] ?? note.type;

  const formattedDate = (() => {
    try {
      return new Date(note.date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return note.date;
    }
  })();

  return (
    <article
      style={{
        background: 'var(--sw-bg-surface, #22223b)',
        borderRadius: 'var(--sw-border-radius, 8px)',
        padding: '14px 16px',
        marginBottom: '10px',
        border: isNew
          ? '1px solid var(--sw-accent-primary, #4cc9f0)'
          : '1px solid var(--sw-border, #2d2d44)',
      }}
    >
      {/* Header row: version badge + new badge + date */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          flexWrap: 'wrap',
        }}
      >
        {/* Type badge */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            background: `${color}22`,
            color,
            letterSpacing: '0.02em',
          }}
        >
          {label}
        </span>

        {/* Version */}
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--sw-text-primary, #e0e0e0)',
          }}
        >
          v{note.version}
        </span>

        {/* New indicator */}
        {isNew && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '1px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 700,
              background: 'var(--sw-accent-primary, #4cc9f0)',
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            New
          </span>
        )}

        {/* Spacer */}
        <span style={{ flex: 1 }} />

        {/* Date */}
        <time
          dateTime={note.date}
          style={{
            fontSize: '11px',
            color: 'var(--sw-text-secondary, #9a9ab0)',
          }}
        >
          {formattedDate}
        </time>
      </div>

      {/* Title */}
      <h3
        style={{
          fontSize: '14px',
          fontWeight: 600,
          margin: '0 0 6px',
          color: 'var(--sw-text-primary, #e0e0e0)',
        }}
      >
        {note.title}
      </h3>

      {/* Body (markdown) */}
      <div
        style={{
          fontSize: '13px',
          lineHeight: 1.5,
          color: 'var(--sw-text-secondary, #9a9ab0)',
        }}
      >
        {renderMarkdown(note.body)}
      </div>
    </article>
  );
}
